import type { GameState } from "../game/types.ts";
import { ensureAuthenticated, getSupabase } from "./supabase.ts";

export type CurrentGameStatus = "active" | "stuck" | "won" | "lost";

let syncTimer: number | null = null;
let pending:
  | { state: GameState; status: CurrentGameStatus }
  | null = null;

function snapshotForDb(state: GameState): Omit<GameState, "undoStack"> & { undoStack?: never } {
  return {
    tableau: state.tableau,
    foundation: state.foundation,
    stock: state.stock,
    waste: state.waste,
    drawMode: state.drawMode,
    stockRecycles: state.stockRecycles,
    scoringSystem: state.scoringSystem,
    score: state.score,
    moves: state.moves,
    startedAt: state.startedAt,
    elapsedMs: state.elapsedMs,
    hintsUsed: state.hintsUsed,
    undosUsed: state.undosUsed,
    finished: state.finished,
    won: state.won,
    seed: state.seed,
    joker: state.joker,
  };
}

function statusFor(state: GameState, requested?: CurrentGameStatus): CurrentGameStatus {
  if (requested) return requested;
  if (state.finished) return state.won ? "won" : "lost";
  return "active";
}

export function scheduleCurrentGameSync(
  state: GameState,
  status?: CurrentGameStatus,
): void {
  pending = { state, status: statusFor(state, status) };
  if (syncTimer != null) window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    const next = pending;
    pending = null;
    syncTimer = null;
    if (next) void syncCurrentGameNow(next.state, next.status);
  }, 900);
}

export async function syncCurrentGameNow(
  state: GameState,
  status?: CurrentGameStatus,
): Promise<void> {
  const sb = getSupabase();
  if (!sb) return;

  const session = await ensureAuthenticated();
  const userId = session?.user.id;
  if (!userId) return;

  const { error } = await sb.from("current_games").upsert(
    {
      user_id: userId,
      seed: state.seed,
      draw_mode: state.drawMode,
      status: statusFor(state, status),
      scoring_system: state.scoringSystem,
      score: state.score,
      moves: state.moves,
      hints_used: state.hintsUsed,
      undos_used: state.undosUsed,
      started_at: Number.isFinite(state.startedAt)
        ? new Date(state.startedAt).toISOString()
        : null,
      updated_at: new Date().toISOString(),
      state: snapshotForDb(state),
    },
    { onConflict: "user_id" },
  );

  if (error) {
    console.warn("[current-game] sync failed:", error.message);
  }
}
